import { Mui } from '@osu/react-ui';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import MenuDialogBase from "./MenuDialogBase";
import { useResumeLaterScholarsApplicantMutation, useGetScholarsApplicantQueueEntriesQuery } from "../../../services/graphql/api";
import { getScholarsApplDetailParams } from "../ScholarsApplicant";
import useChipSubProgram from "../../../util/scholars/useChipSubProgram";
import useRoleAuthentication from "../../../Authentication/hooks/useRoleAuthentication";
import { SCHOLARS_QUEUE_STATUS } from "../../../util/constants";
import { selector as reviewNotesSelector, getSubmittableNotes } from "../../slices/administrativeNotes";

// has "Resume Later" (mirrors the Honors Resume Later action) — flags the applicant the caller is currently
// working (their own IN_REVIEW placement — one they pulled from the queue OR one manually assigned to them)
// as resumeLater, saving their in-progress work and keeping it on their In Progress table without deciding
// it. The scholars program is the one in context (URL ?sp wins, else the PM's own program — same resolver the
// applicant chip uses), i.e. the program the caller is working the applicant under. A caller may hold at
// most 5 in progress per scholars program and must finish one before resuming/pulling more; the backend enforces
// that and returns reason=AT_LIMIT, which we surface here as an informational message (not an error).
export default function DialogResumeLater({ menuItemAndClose }) {
    const params = getScholarsApplDetailParams();
    const { user } = useRoleAuthentication();
    const subProgram = useChipSubProgram();

    // The applicant's queue entries — used to fall back to the caller's OWN IN_REVIEW placement's scholars program
    // when there's no URL/PM chip context (e.g. a reviewer who owns the applicant but arrived without ?sp).
    const { data: queueEntries = [] } = useGetScholarsApplicantQueueEntriesQuery(
        { applicantEmployeeId: params?.applicantEmployeeId, term: params?.term },
        { skip: !params?.applicantEmployeeId }
    );
    const ownedEntry = (queueEntries ?? []).find(
        (e) => e?.status === SCHOLARS_QUEUE_STATUS.IN_REVIEW.KEY && e?.reviewerEmployeeId === user?.osuid
    );

    // Prefer the chip's scholars program context; otherwise use the caller's own placement's scholars program.
    const subProgramId = subProgram?.subProgramId ?? ownedEntry?.subProgramId;
    const subProgramLabel = subProgram?.label ?? subProgramId;

    // The reviewer's checked Administrative Notes (same Redux slice the Notes section drives). Persisted as
    // the draft so the checkboxes re-populate when they resume — this is the "save my work so far" half.
    const noteState = useSelector(reviewNotesSelector);
    const administrativeNotes = getSubmittableNotes(noteState ?? []);

    const [apiCall, result] = useResumeLaterScholarsApplicantMutation();
    const navigate = useNavigate();

    const variables = { applicantEmployeeId: params?.applicantEmployeeId, subProgramId, administrativeNotes };
    const isDisabled = !subProgramId;
    const buttons = [{ label: "Save", apiCall, variables, isDisabled }];

    // Soft cap outcome: the mutation "succeeds" (no error) but returns reason=AT_LIMIT with no status.
    const atLimit = result?.isSuccess && result?.data?.reason === "AT_LIMIT";

    // On a genuine resume-later save (success WITH a status, i.e. not the AT_LIMIT soft outcome), take the
    // reviewer back to the queue — scoped to the scholars program they were working (mirrors ScholarsDecisionPanel).
    const saved = result?.isSuccess && !!result?.data?.status;
    useEffect(() => {
        if (!saved) { return; }
        menuItemAndClose?.close?.();
        navigate(subProgramId ? `/scholars/queue/${subProgramId}` : "/scholars/queue");
    }, [saved, subProgramId, navigate, menuItemAndClose]);

    const message = subProgramId
        ? `You are saving your work and will be able to resume later.`
        : "This applicant has no program in context. Open it from a program's queue (or your In Progress table) so it can be resumed under that program.";

    return (
        <MenuDialogBase menuItemAndClose={menuItemAndClose}
            apiCall={apiCall}
            buttons={buttons}
            result={result}
            extraContent={(
                <>
                    <Mui.Typography tabIndex={0} sx={{ marginBottom: 0 }}>{message}</Mui.Typography>

                    {atLimit && (
                        <Mui.Alert severity="info" sx={{ marginTop: "1rem" }} tabIndex={0}>
                            You have reached the maximum of 5 in-progress applications for {subProgramLabel}.
                            Finish one before resuming another.
                        </Mui.Alert>
                    )}
                </>
            )}
        />
    );
}
